import { useEffect, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import { CountriesList, Employee } from "../types/processTypes";
import { fetchRegionOptions } from "./regionAutocompleteUtils";

interface RegionAutocompleteProps {
  formikProps: FormikProps<Employee>;
}

export const RegionAutocomplete = ({ formikProps }: RegionAutocompleteProps) => {
  const { t } = useTranslation();
  const regionValue = formikProps.values.address?.region ?? "";
  const [inputValue, setInputValue] = useState(regionValue);
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setInputValue(regionValue);
  }, [regionValue]);

  useEffect(() => {
    if (!open) return;

    let active = true;
    setLoading(true);

    const timer = setTimeout(async () => {
      try {
        const nextOptions = await fetchRegionOptions(inputValue);
        if (!active) return;
        setOptions(nextOptions);
      } catch {
        if (active) setOptions([]);
      } finally {
        if (active) setLoading(false);
      }
    }, 200);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [inputValue, open]);

  const setRegion = (value: string) => {
    const trimmed = value.trim();
    const currentAddress = formikProps.values.address ?? {
      region: "",
      city: "",
      telephone: "",
      country: CountriesList.ethiopia,
    };
    formikProps.setFieldValue("address", {
      ...currentAddress,
      region: trimmed,
    });
  };

  return (
    <Autocomplete
      freeSolo
      fullWidth
      size="small"
      open={open}
      openOnFocus
      options={options}
      loading={loading}
      filterOptions={(x) => x}
      autoHighlight
      value={regionValue}
      inputValue={inputValue}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onInputChange={(_, newValue, reason) => {
        if (reason === "reset") return;
        setInputValue(newValue);
        setRegion(newValue);
        if (reason === "input") setOpen(true);
      }}
      onChange={(_, newValue) => {
        const next = typeof newValue === "string" ? newValue : "";
        setInputValue(next);
        setRegion(next);
      }}
      onBlur={() => {
        setRegion(inputValue);
        setOpen(false);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t("region")}
          sx={{ mt: 1 }}
          InputLabelProps={{ shrink: true }}
        />
      )}
    />
  );
};
